import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'
import { buildCruiseOfferDrawerContext } from '../../utils/drawerAskAIContext'
import useCruiseOfferDetail from './useCruiseOfferDetail'
import CruiseOfferDrawerContent from './CruiseOfferDrawerContent'
import DrawerDetailLoadingSkeleton from '../DrawerDetailLoadingSkeleton'
import { drawerBodyEmptyStatePaddingStyles } from '../drawerBodyLayout.styles'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildOfferDrawerOpenedActivityEntry } from '../buildOfferDrawerOpenedActivityEntry'

const ErrorWrapper = styled(Group)`
  ${drawerBodyEmptyStatePaddingStyles}
  text-align: center;
`

interface Props {
  offerId: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  itineraryData?: TripItineraryData
  isStreaming?: boolean
  onSendMessage?: (message: string) => Promise<void>
}

function ChatCruiseOfferDetailDrawer({
  offerId,
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  itineraryData,
  isStreaming,
  onSendMessage,
}: Props) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const { offer, isLoading, isError, refetch } = useCruiseOfferDetail({
    offerId: isOpen ? offerId : undefined,
    enabled: isOpen,
  })
  const assistantContextDetails = useMemo(
    () => offer ? buildCruiseOfferDrawerContext(offer) : undefined,
    [offer],
  )

  const eventBus = useTripPlannerEventBusOptional()
  // Fresh mount per genuine drawer open (DrawerContainer.tsx keys this component on the drawer
  // stack entry id), so a plain per-mount ref is enough -- see ChatOfferDetailDrawer.tsx's
  // identical guard for the full reasoning. Matters especially here: offer can already be
  // synchronously truthy on mount if this cruise was fetched earlier for another purpose (e.g.
  // search results), since it's read straight off Redux state rather than a fresh query.
  const dispatchedOfferDrawerOpenRef = useRef(false)
  useEffect(() => {
    if (dispatchedOfferDrawerOpenRef.current || !offer) return
    dispatchedOfferDrawerOpenRef.current = true
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildOfferDrawerOpenedActivityEntry(offer.name, 'cruise', offerId)],
    })
  }, [offer, eventBus, offerId])
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const drawerHeader = useMemo(() => {
    if (!offer || showTitleInContent) return undefined

    return <TripDrawerHeaderTitle title={offer.name} />
  }, [offer, showTitleInContent])

  const failedToLoadLabel = intl.formatMessage({
    id: '6kpaAaSscznWj0opFA-_',
    defaultMessage: 'Failed to load cruise details.',
    description:
      'Error message shown when cruise offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description:
      'Button label to retry loading hotel offer details after an error',
  })
  const assistantPlaceholder = intl.formatMessage({
    id: 'JylXClObuWPEpz2RSLtM',
    defaultMessage: 'Ask about this cruise',
    description:
      'Short placeholder text for the embedded Ask AI input in the cruise offer drawer',
  })
  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={offer?.name ?? 'Cruise offer'}
          contextDetails={assistantContextDetails}
          isOpen={isOpen && !!offer}
          placeholder={assistantPlaceholder}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        contextLabel={offer?.name ?? 'Cruise offer'}
        contextDetails={assistantContextDetails}
        isOpen={isOpen && !!offer}
      />
      {isLoading && (
        <DrawerDetailLoadingSkeleton />
      )}
      {isError && !isLoading && !offer && (
        <ErrorWrapper
          direction="vertical"
          gap={8}
          horizontalAlign="center"
          verticalAlign="center"
        >
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={handleRetry}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {offer && <CruiseOfferDrawerContent
        key={offer.id}
        offer={offer}
        itineraryData={itineraryData}
        isStreaming={isStreaming}
        onSendMessage={onSendMessage}
        showTitleInContent={showTitleInContent}
      />}
    </TripDrawer>
  )
}

export default ChatCruiseOfferDetailDrawer
